import { jsPDF } from "jspdf";
import { parseBoldSegments } from "../parser/text-parser";
import { getLineHeight, PDF_TABLE_FONT_SIZE } from "../constants";
import { fontManager } from "../font/font-manager";

/** 세그먼트 배열의 총 너비 계산 (bold/regular 폰트 고려) */
const calculateSegmentsWidth = (
  segments: { text: string; bold: boolean }[],
  isHeader: boolean,
  pdf: jsPDF,
  fontLoaded: boolean
): number => {
  let totalWidth = 0;
  for (const seg of segments) {
    // 텍스트 내용에 맞는 폰트 선택
    fontManager.setFont(pdf, isHeader || seg.bold, fontLoaded, seg.text);
    pdf.setFontSize(PDF_TABLE_FONT_SIZE);
    totalWidth += pdf.getTextWidth(seg.text);
  }
  return totalWidth;
};

/** 긴 단어를 maxWidth에 맞게 분할 (높이 계산용) */
const splitLongWordForCount = (
  word: string,
  maxWidth: number,
  isHeader: boolean,
  isBold: boolean,
  pdf: jsPDF,
  fontLoaded: boolean
): string[] => {
  fontManager.setFont(pdf, isHeader || isBold, fontLoaded, word);
  pdf.setFontSize(PDF_TABLE_FONT_SIZE);
  
  const wordWidth = pdf.getTextWidth(word);
  if (wordWidth <= maxWidth) {
    return [word];
  }
  
  const result: string[] = [];
  let currentPart = "";
  
  for (const char of word) {
    const testPart = currentPart + char;
    const testWidth = pdf.getTextWidth(testPart);
    
    if (testWidth <= maxWidth) {
      currentPart = testPart;
    } else {
      if (currentPart) {
        result.push(currentPart);
      }
      currentPart = char;
    }
  }
  
  if (currentPart) {
    result.push(currentPart);
  }
  
  return result;
};

/** 텍스트 줄바꿈 시 필요한 줄 수 계산 */
const countTextLines = (
  text: string,
  maxWidth: number,
  isHeader: boolean,
  pdf: jsPDF,
  fontLoaded: boolean
): number => {
  const segments = parseBoldSegments(text);
  let lineCount = 0;
  let currentSegments: { text: string; bold: boolean }[] = [];

  for (const seg of segments) {
    const words = seg.text.split(" ");
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const spaceAfter = i < words.length - 1 ? " " : "";
      
      // 긴 단어 분할 처리
      const wordParts = splitLongWordForCount(word, maxWidth, isHeader, seg.bold, pdf, fontLoaded);
      
      for (let j = 0; j < wordParts.length; j++) {
        const isLastPart = j === wordParts.length - 1;
        const wordWithSpace = wordParts[j] + (isLastPart ? spaceAfter : "");
        
        const testSegments = [...currentSegments, { text: wordWithSpace, bold: seg.bold }];
        const testWidth = calculateSegmentsWidth(testSegments, isHeader, pdf, fontLoaded);

        if (testWidth <= maxWidth) {
          currentSegments.push({ text: wordWithSpace, bold: seg.bold });
        } else {
          if (currentSegments.length > 0) lineCount++;
          currentSegments = [{ text: wordWithSpace, bold: seg.bold }];
        }
      }
    }
  }
  if (currentSegments.length > 0) lineCount++;
  return lineCount;
};

/** 테이블 셀의 높이 계산 (• 항목 여러 개 포함 시 합산) */
export const calculateCellHeight = (
  cell: string,
  colWidth: number,
  cellPad: number,
  isHeader: boolean,
  pdf: jsPDF,
  fontLoaded: boolean
): number => {
  const w = colWidth - cellPad * 2;
  const cellLineHeight = getLineHeight(PDF_TABLE_FONT_SIZE);

  fontManager.setFont(pdf, isHeader, fontLoaded, cell);
  pdf.setFontSize(PDF_TABLE_FONT_SIZE);

  const bulletItems = (cell || "").split(/(?=• )/g).filter((part) => part.trim());
  const totalLineCount = bulletItems.reduce(
    (sum, item) => sum + countTextLines(item.trim(), w, isHeader, pdf, fontLoaded),
    0
  );

  return Math.max(totalLineCount * cellLineHeight, cellLineHeight);
};

/** 긴 단어를 maxWidth에 맞게 분할 */
const splitLongWord = (
  word: string,
  maxWidth: number,
  isHeader: boolean,
  isBold: boolean,
  pdf: jsPDF,
  fontLoaded: boolean
): string[] => {
  fontManager.setFont(pdf, isHeader || isBold, fontLoaded, word);
  pdf.setFontSize(PDF_TABLE_FONT_SIZE);
  
  const wordWidth = pdf.getTextWidth(word);
  if (wordWidth <= maxWidth) {
    return [word];
  }
  
  // 단어를 문자 단위로 분할
  const result: string[] = [];
  let currentPart = "";
  
  for (const char of word) {
    const testPart = currentPart + char;
    const testWidth = pdf.getTextWidth(testPart);
    
    if (testWidth <= maxWidth) {
      currentPart = testPart;
    } else {
      if (currentPart) {
        result.push(currentPart);
      }
      currentPart = char;
    }
  }
  
  if (currentPart) {
    result.push(currentPart);
  }
  
  return result;
};

/** 세그먼트를 렌더링하고 마지막 Y 좌표 반환 */
const renderSegmentsAtY = (
  text: string,
  cellX: number,
  startY: number,
  maxWidth: number,
  isHeader: boolean,
  pdf: jsPDF,
  fontLoaded: boolean
): number => {
  const cellLineHeight = getLineHeight(PDF_TABLE_FONT_SIZE);
  const segments = parseBoldSegments(text);
  let currentY = startY;
  let currentSegments: { text: string; bold: boolean }[] = [];

  const flushLine = () => {
    let curX = cellX;
    for (const cseg of currentSegments) {
      // 텍스트 내용에 맞는 폰트 선택
      fontManager.setFont(pdf, isHeader || cseg.bold, fontLoaded, cseg.text);
      pdf.setFontSize(PDF_TABLE_FONT_SIZE);
      const segWidth = pdf.getTextWidth(cseg.text);
      pdf.text(cseg.text, curX, currentY);
      curX += segWidth;
    }
    currentY += cellLineHeight;
  };

  for (const seg of segments) {
    const words = seg.text.split(" ");
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const spaceAfter = i < words.length - 1 ? " " : "";
      
      // 긴 단어는 분할 처리
      const wordParts = splitLongWord(word, maxWidth, isHeader, seg.bold, pdf, fontLoaded);
      
      for (let j = 0; j < wordParts.length; j++) {
        const isLastPart = j === wordParts.length - 1;
        const wordWithSpace = wordParts[j] + (isLastPart ? spaceAfter : "");
        
        const testSegments = [...currentSegments, { text: wordWithSpace, bold: seg.bold }];
        const testWidth = calculateSegmentsWidth(
          testSegments.map((s) => ({ text: s.text, bold: s.bold })),
          isHeader,
          pdf,
          fontLoaded
        );

        if (testWidth <= maxWidth) {
          currentSegments.push({ text: wordWithSpace, bold: seg.bold });
        } else {
          if (currentSegments.length > 0) flushLine();
          currentSegments = [{ text: wordWithSpace, bold: seg.bold }];
        }
      }
    }
  }
  if (currentSegments.length > 0) flushLine();
  return currentY;
};

/** 테이블 셀 렌더링 (• 항목 분리 처리), 셀 하단 Y 좌표 반환 */
export const renderCell = (
  cell: string,
  colWidth: number,
  cellPad: number,
  cellX: number,
  cellY: number,
  isHeader: boolean,
  pdf: jsPDF,
  fontLoaded: boolean
): number => {
  const w = colWidth - cellPad * 2;

  fontManager.setFont(pdf, isHeader, fontLoaded, cell);
  pdf.setFontSize(PDF_TABLE_FONT_SIZE);

  const bulletItems = (cell || "").split(/(?=• )/g).filter((part) => part.trim());
  let currentY = cellY;

  for (const item of bulletItems) {
    currentY = renderSegmentsAtY(item.trim(), cellX, currentY, w, isHeader, pdf, fontLoaded);
  }

  return currentY;
};
